import React from 'react';
import { FlatList, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDeals } from '../../src/data';
import { GROUPS, type Group, groupOf, price, sourceNames, timeAgo } from '../../src/format';
import { C } from '../../src/theme';
import type { Deal } from '../../src/types';

/**
 * A안 — Impeccable / Operate 모드. "도구는 작업 뒤로 사라진다".
 *
 * 지금 디자인의 문제를 정보 설계 쪽에서 고친다.
 *  - 카드를 없앤다. 딜은 목록이지 카드 더미가 아니다. 머리카락 굵기 구분선만 둔다.
 *  - 점수 타일을 뺀다. 전부 100점이라 아무것도 말해주지 않았다.
 *    할인율을 아는 딜에만 그 숫자를 보여 준다 — 실제로 판단에 쓰이는 유일한 수치다.
 *  - "시장가 미확인" 같은 빈 문구를 지운다. 모르면 아무 말도 하지 않는다.
 *  - 가격은 오른쪽에 자릿수 고정으로 세워 세로로 비교된다.
 * 결과적으로 한 화면에 들어오는 딜이 3개에서 8개로 늘어난다.
 */
export default function DesignA() {
  const insets = useSafeAreaInsets();
  const deals = useDeals();
  const [group, setGroup] = React.useState<Group>('전체');
  const shown = React.useMemo(
    () => (group === '전체' ? deals : deals.filter((d) => groupOf(d) === group)),
    [deals, group],
  );

  return (
    <View style={{ flex: 1, backgroundColor: C.card }}>
      <View style={[s.head, { paddingTop: insets.top + 14 }]}>
        <Text style={s.h1}>오늘의 딜</Text>
        <Text style={s.count}>
          {shown.length}건<Text style={{ color: C.faint }}> · 5분마다 갱신</Text>
        </Text>
      </View>
      <View style={s.tabs}>
        {GROUPS.map((g) => (
          <Pressable key={g} onPress={() => setGroup(g)} style={[s.tab, g === group && s.tabOn]}>
            <Text style={[s.tabText, g === group && s.tabTextOn]}>{g}</Text>
          </Pressable>
        ))}
      </View>
      <FlatList
        data={shown}
        keyExtractor={(d) => d.id}
        contentContainerStyle={{ paddingBottom: 24 }}
        renderItem={({ item }) => <Row deal={item} />}
        ItemSeparatorComponent={() => <View style={s.rule} />}
      />
    </View>
  );
}

/** 시장가를 아는 딜만 할인율을 돌려준다. 모르면 null — 화면에 자리를 만들지 않는다. */
function discount(d: Deal): number | null {
  if (!d.market_price || !d.effective_price) return null;
  const pct = Math.round((1 - d.effective_price / d.market_price) * 100);
  return pct >= 5 ? pct : null;
}

function Row({ deal }: { deal: Deal }) {
  const off = discount(deal);
  const free = deal.shipping_krw === 0;
  return (
    <Pressable style={({ pressed }) => [s.row, pressed && { backgroundColor: C.line }]}>
      <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
        <Text style={s.title} numberOfLines={2}>
          {deal.product}
        </Text>
        <Text style={s.meta} numberOfLines={1}>
          {deal.mall ?? '판매처 미확인'}
          <Text style={{ color: C.faint }}>
            {'   '}
            {sourceNames(deal.sources)}
            {deal.posted_at ? `   ${timeAgo(deal.posted_at)}` : ''}
          </Text>
        </Text>
      </View>
      <View style={{ alignItems: 'flex-end', gap: 3 }}>
        <Text style={s.price}>{price(deal)}</Text>
        {off !== null ? (
          <Text style={s.off}>{off}% 싸게</Text>
        ) : free ? (
          <Text style={s.ship}>무료배송</Text>
        ) : null}
      </View>
    </Pressable>
  );
}

const s = StyleSheet.create({
  head: { paddingHorizontal: 20, paddingBottom: 12, gap: 2 },
  h1: { fontSize: 24, fontWeight: '700', color: C.text, letterSpacing: -0.5 },
  count: { fontSize: 13, color: C.text3, fontVariant: ['tabular-nums'] },
  // 알약 칩 대신 밑줄 탭. 선택 상태가 색이 아니라 위치로 읽힌다
  tabs: { flexDirection: 'row', paddingHorizontal: 20, gap: 18, borderBottomWidth: 1, borderBottomColor: C.line },
  tab: { paddingVertical: 10, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabOn: { borderBottomColor: C.text },
  tabText: { fontSize: 14, fontWeight: '500', color: C.muted },
  tabTextOn: { color: C.text, fontWeight: '700' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 20, paddingVertical: 13 },
  rule: { height: 1, marginLeft: 20, backgroundColor: C.line },
  title: { fontSize: 15, fontWeight: '500', lineHeight: 20, color: C.text },
  meta: { fontSize: 12, color: C.text3 },
  price: { fontSize: 17, fontWeight: '700', color: C.text, fontVariant: ['tabular-nums'], letterSpacing: -0.3 },
  off: { fontSize: 12, fontWeight: '700', color: C.good },
  ship: { fontSize: 12, color: C.muted },
});
